/**
 * Sticker/medal catalog — text-FREE reward for ages 2-6.
 *
 * Each activity (mini-game or song) has its own sticker; completing it awards
 * the sticker through `progressStore.awardSticker(id)` and adds it to the
 * persistent collection (/collection). The child does not read: what they
 * recognize is the emoji/icon.
 *
 * ID convention:
 *   - games:    "game:<gameId>"     (one per mini-game)
 *   - songs:    "song:<songId>"     (one per learned song)
 * Keep stable: the ID is what persists in localStorage.
 */

export interface Sticker {
  id: string;
  /** Emoji rendered as a medal (without relying on text). */
  icon: string;
}

/** Sticker for a completed mini-game. */
export function gameStickerId(gameId: string): string {
  return `game:${gameId}`;
}

/** Sticker for a learned song. */
export function songStickerId(songId: string): string {
  return `song:${songId}`;
}

/** Default icon for an unknown sticker (forward compatibility). */
const FALLBACK_ICON = "⭐";

/** Icons by known ID; songs share the music icon. */
const STICKER_ICONS: Record<string, string> = {
  "game:simon": "🐵",
  "game:who-sounds": "👂",
  "game:count-and-play": "🔢",
};

/** Resolves a sticker's icon by its ID (stable and text-free). */
export function stickerIcon(stickerId: string): string {
  const known = STICKER_ICONS[stickerId];
  if (known !== undefined) {
    return known;
  }
  if (stickerId.startsWith("song:")) {
    return "🎵";
  }
  return FALLBACK_ICON;
}

/** Builds the Sticker object (ID + icon) for rendering in the collection. */
export function toSticker(stickerId: string): Sticker {
  return { id: stickerId, icon: stickerIcon(stickerId) };
}
